import { InputHTMLAttributes, ReactNode, forwardRef, useId } from "react";
import styles from "./Input.module.css";

export type HelperVariant = "default" | "warn" | "error" | "ok";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Block-level helper text rendered under the input. */
  hint?: string;
  /** Error message — when present, takes precedence over `hint`. */
  error?: string;
  /** Right-aligned inline hint rendered next to the label. */
  hintRight?: ReactNode;
  /** Optional leading icon (rendered absolutely positioned at the left). */
  leadingIcon?: ReactNode;
  /** Optional trailing icon/button (rendered absolutely positioned at the right). */
  trailingIcon?: ReactNode;
  /** Render a warn-coloured border to signal a pending unsaved change. */
  modified?: boolean;
  /** Visual variant for the helper text (`hint`). */
  helperVariant?: HelperVariant;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    hintRight,
    leadingIcon,
    trailingIcon,
    modified,
    helperVariant = "default",
    id,
    className = "",
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const fid = id ?? reactId;

  const helperClass =
    helperVariant === "warn"
      ? styles.helperWarn
      : helperVariant === "error"
        ? styles.helperError
        : helperVariant === "ok"
          ? styles.helperOk
          : styles.helper;

  const inputClass = [
    styles.input,
    leadingIcon ? styles.inputWithLeading : "",
    trailingIcon ? styles.inputWithTrailing : "",
    error ? styles.error : "",
    !error && modified ? styles.modified : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.wrap}>
      {(label || hintRight) && (
        <div className={hintRight ? styles.labelRow : undefined}>
          {label && (
            <label className={styles.label} htmlFor={fid}>
              {label}
            </label>
          )}
          {hintRight && <span className={styles.hintRight}>{hintRight}</span>}
        </div>
      )}
      <div className={styles.inputWrap}>
        {leadingIcon && (
          <span className={styles.leading} aria-hidden>
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={fid}
          className={inputClass}
          aria-invalid={!!error || undefined}
          aria-describedby={
            error ? `${fid}-err` : hint ? `${fid}-hint` : undefined
          }
          {...rest}
        />
        {trailingIcon && <span className={styles.trailing}>{trailingIcon}</span>}
      </div>
      {error && (
        <div id={`${fid}-err`} className={styles.errorText}>
          {error}
        </div>
      )}
      {!error && hint && (
        <div id={`${fid}-hint`} className={helperClass}>
          {hint}
        </div>
      )}
    </div>
  );
});
